import { useState, useEffect, useCallback, useMemo } from "react";

import { getMyProfile, updateMyProfile } from "@/api/profileApi";
import {
  parseDateParts,
  sortExperienceOrEducationList,
  DegreeEnum,
} from "@/pages/Profile/utils";
import { getDaysSince, formatLocalYmd } from "@/utils/dateTime";
import { ProfileFields } from "@/constants/ApiEndpoints";

export const useProfileData = () => {
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Personal information state.
   * Initialized with safe defaults to avoid undefined access in UI.
   */
  const [personalInfo, setPersonalInfo] = useState({
    emails: [],
    completedTraining: [],
    updatedTimestamp: null,
  });

  /**
   * Work experience list state.
   */
  const [experienceList, setExperienceList] = useState([]);

  /**
   * Education list state.
   */
  const [educationList, setEducationList] = useState([]);

  /**
   * Core mapping logic that transforms the backend Profile response
   * into frontend-friendly state structures.
   *
   * Wrapped with useCallback to avoid unnecessary re-renders.
   */
  const mapDataToState = useCallback((profile) => {
    if (!profile?.user) return;

    const { user, workHistory = [], education = [], training = [] } = profile;
    const currentJob = workHistory.find((exp) => exp.isCurrentJob) || {};

    /**
     * Map user data to personalInfo state.
     */
    const emailList = [];

    if (user.primaryEmail) {
      emailList.push({
        id: "primary",
        email: user.primaryEmail,
        isPrimary: true,
      });
    }

    if (Array.isArray(user.alternativeEmails)) {
      user.alternativeEmails.forEach((email, idx) => {
        emailList.push({
          id: `alt-${idx}`,
          email,
          isPrimary: false,
        });
      });
    }

    setPersonalInfo((prev) => ({
      ...prev,
      id: user.id,
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      preferredName: user.preferredName || "",
      timezone: user.timezone || "",
      linkedin: user.linkedinLink || "",
      communicationMethod: user.communicationMethod,
      timezoneUpdatedAt: user.timezoneUpdatedAt,
      emails: emailList,
      title: currentJob.title || "",
      company: currentJob.companyOrOrganization || "",
      completedTraining: training.map((t) => ({
        id: t.id,
        category: t.category,
        status: t.status,
        // Pass the API timestamps through unchanged. TrainingSection wants
        // the actual day, not the bare month/year that parseDateParts
        // returned.
        completedTimestamp: t.completedTimestamp,
        deadline: t.deadline,
        link: t.link,
      })),
    }));

    /**
     * Map workHistory list.
     */
    const mappedExperience = workHistory
      .map((exp) => {
        const startParts = parseDateParts(exp.startDate);
        const endParts = parseDateParts(exp.endDate);

        return {
          id: exp.id,
          title: exp.title,
          company: exp.companyOrOrganization,
          startMonth: startParts.month,
          startYear: startParts.year,
          endMonth: endParts.month,
          endYear: endParts.year,
          isCurrentlyWorking: exp.isCurrentJob,
        };
      })
      .sort(sortExperienceOrEducationList);

    setExperienceList(mappedExperience);

    /**
     * Map education list.
     */
    const mappedEducation = education
      .map((edu) => {
        const startParts = parseDateParts(edu.startDate);
        const endParts = parseDateParts(edu.endDate);

        return {
          id: edu.id,
          institution: edu.school,
          degree: Object.values(DegreeEnum).includes(edu.degree)
            ? edu.degree
            : "",
          field: edu.fieldOfStudy,
          startMonth: startParts.month,
          startYear: startParts.year,
          endMonth: endParts.month,
          endYear: endParts.year,
        };
      })
      .sort(sortExperienceOrEducationList);

    setEducationList(mappedEducation);
  }, []);

  /**
   * Fetch profile data from backend.
   */
  const fetchProfileData = useCallback(async () => {
    setIsLoading(true);
    try {
      const {
        data: { profile },
      } = await getMyProfile({
        fields: [
          ProfileFields.WORK_HISTORY,
          ProfileFields.EDUCATION,
          ProfileFields.TRAINING,
        ],
      });

      if (profile) {
        mapDataToState(profile);
      } else {
        console.warn("Profile data structure is unexpected:", profile);
      }
    } catch (error) {
      console.error("Failed to fetch profile:", error);
    } finally {
      setIsLoading(false);
    }
  }, [mapDataToState]);

  /**
   * Generic update handler for profile-related updates.
   * Used by edit modals. Updates local state on success
   * without requiring a full refresh.
   */
  const handleUpdateProfile = async (payload) => {
    const {
      data: { profile },
    } = await updateMyProfile(payload);

    if (profile) {
      mapDataToState(profile);
    }

    return profile;
  };

  /**
   * Computed flag indicating whether user's timezone can be edited.
   * Editing is restricted to once every 30 days.
   */
  const canEditTimezone = useMemo(() => {
    if (!personalInfo.timezoneUpdatedAt) return true;

    const days = getDaysSince(personalInfo.timezoneUpdatedAt);
    return days >= 30;
  }, [personalInfo.timezoneUpdatedAt]);

  /**
   * Computed string representing the next available edit date.
   */
  const nextEditableDate = useMemo(() => {
    if (!personalInfo.timezoneUpdatedAt) return "";

    const date = new Date(personalInfo.timezoneUpdatedAt);
    date.setDate(date.getDate() + 30);

    return formatLocalYmd(date);
  }, [personalInfo.timezoneUpdatedAt]);

  /**
   * Initial data load.
   */
  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData]);

  /**
   * Public hook API.
   */
  return {
    isLoading,
    personalInfo,
    experienceList,
    educationList,
    canEditTimezone,
    nextEditableDate,
    handleUpdateProfile,
    refresh: fetchProfileData,
  };
};
